import { createFileRoute } from "@tanstack/react-router";
import {
  Check,
  X,
  Clock,
  ArrowRight,
  BadgeDollarSign,
  Info,
  ExternalLink,
  CircleDashed,
} from "lucide-react";

import { AdminShell } from "@/components/AdminShell";
import { PLANS } from "@/lib/plans";
import { BlocketGaps } from "@/components/admin/BlocketGaps";
import { ourEdge } from "@/lib/blocket-gaps";
import { CapacityAnalysis } from "@/components/admin/CapacityAnalysis";
import { CategoryFieldPlan } from "@/components/admin/CategoryFieldPlan";
import { ExternalCostSummary } from "@/components/admin/ExternalCostSummary";



export const Route = createFileRoute("/_authenticated/admin/blocket")({
  head: () => ({
    meta: [
      { title: "Sellavo vs Blocket — Administration" },
      {
        name: "description",
        content:
          "Intern jämförelse: Blockets annonstyper och avgifter mot Sellavos flöde och priser.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <AdminShell title="Sellavo vs Blocket">
      <Comparison />
    </AdminShell>
  ),
});

const blocketSteps = [
  "Skapa konto / logga in med BankID",
  "Välj «Lägg in annons»",
  "Välj huvudkategori",
  "Välj underkategori",
  "Välj underkategori igen (nivå 3 i vissa fack)",
  "Fyll i rubrik manuellt",
  "Skriv hela beskrivningen själv",
  "Fyll i skick, märke, modell och andra fältkrav",
  "Sätt pris helt på egen gissning",
  "Fyll i ort och postnummer",
  "Ladda upp bilder (en och en, ingen hjälp med format)",
  "Välj annonspaket (Bas / Plus / Premium)",
  "Ta ställning till betalning och moms/proffs-fråga",
  "Granska och publicera",
  "Vänta på granskning innan annonsen syns",
];

const sellavoSteps = [
  "Fota prylen (upp till 3 bilder, alla vanliga format funkar)",
  "Texten, kategorin och prisförslaget kommer färdigt",
  "Justera vid behov, välj ort och publicera",
];

/** Blockets prislista för privatannonser (frånpris, per annons). */
const paidCategories: { group: string; items: { name: string; price: string }[] }[] = [
  {
    group: "Bil & husvagn",
    items: [
      { name: "Bil", price: "från 249 kr" },
      { name: "Husvagn", price: "från 249 kr" },
      { name: "Husbil", price: "från 249 kr" },
      { name: "A-traktor", price: "från 209 kr" },
      { name: "Släp och trailer", price: "från 169 kr" },
      { name: "Bildelar och tillbehör", price: "från 89 kr" },
      { name: "Husvagns- och husbilsdelar", price: "från 89 kr" },
    ],
  },
  {
    group: "Båt",
    items: [
      { name: "Båt", price: "från 249 kr" },
      { name: "Båttrailer och båtvagn", price: "från 169 kr" },
      { name: "Båtplats och förvaring", price: "från 139 kr" },
      { name: "Båtmotor", price: "från 139 kr" },
      { name: "Båtdelar och tillbehör", price: "från 89 kr" },
    ],
  },
  {
    group: "MC, moped & skoter",
    items: [
      { name: "Motorcykel", price: "från 209 kr" },
      { name: "Moped och mopedbil", price: "från 209 kr" },
      { name: "ATV", price: "från 209 kr" },
      { name: "Snöskoter", price: "från 209 kr" },
      { name: "MC- och ATV-släp", price: "från 169 kr" },
      { name: "MC-utrustning och reservdelar", price: "från 89 kr" },
      { name: "ATV-reservdelar", price: "från 89 kr" },
    ],
  },
  {
    group: "Maskiner & tunga fordon",
    items: [
      { name: "Lastbil och släpvagn", price: "från 249 kr" },
      { name: "Buss", price: "från 249 kr" },
      { name: "Entreprenad och anläggning", price: "från 249 kr" },
      { name: "Traktor", price: "från 209 kr" },
      { name: "Skördetröska", price: "från 209 kr" },
      { name: "Skogs- och lantbruksmaskiner", price: "från 209 kr" },
    ],
  },
  {
    group: "Övrigt betalt",
    items: [
      { name: "Affärsverksamhet (alla underkategorier)", price: "från 139 kr" },
      { name: "Bostad – privat uthyrning/försäljning", price: "hanteras via Qasa, egen prislista" },
      { name: "Jobbannons", price: "från ca 795 kr" },
    ],
  },
];

const freeCategories = [
  "Möbler och heminredning",
  "Elektronik och datorer",
  "Kläder och accessoarer",
  "Barnartiklar och leksaker",
  "Sport och fritid",
  "Verktyg och byggmaterial",
  "Hobby och samlarprylar",
  "Djurtillbehör",
];

const packages: { name: string; segment: string; contains: string }[] = [
  {
    name: "Bas / Standard",
    segment: "Torget + fordon",
    contains:
      "Annonsperiod 365 dagar på Torget, notis till bevakare (push direkt, mejl kvällen efter). Ingen extra synlighet.",
  },
  {
    name: "Plus",
    segment: "Torget",
    contains: "Högst upp i listan i 7 dagar från publicering.",
  },
  {
    name: "Premium",
    segment: "Torget",
    contains: "Högst upp i listan i 7 dagar + plats bland rekommenderade annonser i 7 dagar.",
  },
  {
    name: "Plus",
    segment: "Fordon",
    contains: "Högst upp i listan i 3 dagar (från dag 3) + 1 automatisk boost dag 7.",
  },
  {
    name: "Premium",
    segment: "Fordon",
    contains:
      "Högst upp i listan i 5 dagar (från dag 3) + 3 automatiska boostar (dag 7, 14 och 21).",
  },
];

type RowStatus = "battre" | "lika" | "delvis" | "sämre";

const rowStatusLabel: Record<RowStatus, string> = {
  battre: "Bättre hos oss",
  lika: "Vi har det också",
  delvis: "Delvis – inte skarpt",
  sämre: "Blocket vinner",
};

const rowStatusClass: Record<RowStatus, string> = {
  battre: "bg-primary/20 text-primary",
  lika: "bg-primary/10 text-primary",
  delvis: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  sämre: "bg-destructive/10 text-destructive",
};

const rowStatusIcon: Record<RowStatus, typeof Check> = {
  battre: Check,
  lika: Check,
  delvis: CircleDashed,
  sämre: X,
};

const rowStatusIconClass: Record<RowStatus, string> = {
  battre: "text-primary",
  lika: "text-primary",
  delvis: "text-amber-500",
  sämre: "text-destructive",
};



const rows: {
  label: string;
  blocket: string;
  sellavo: string;
  status: RowStatus;
  note?: string;
}[] = [
  { label: "Steg för att lägga ut", blocket: "≈15 steg", sellavo: "3 steg", status: "battre" },
  {
    label: "Tid till publicerad annons",
    blocket: "10–20 min",
    sellavo: "under 1 minut",
    status: "battre",
  },
  {
    label: "Skriva annonstext",
    blocket: "Du skriver allt själv",
    sellavo: "Färdig text direkt",
    status: "battre",
  },
  { label: "Prissättning", blocket: "Du gissar", sellavo: "Prisförslag ingår", status: "battre" },
  {
    label: "Djupvärdering av objektet",
    blocket: "Finns inte",
    sellavo: "Genomgång med prisintervall och rekommenderat reservationspris",
    status: "battre",
  },
  {
    label: "Kategorival",
    blocket: "2–3 nivåer manuellt",
    sellavo: "Föreslås automatiskt",
    status: "battre",
  },
  {
    label: "Bilder",
    blocket: "Manuell uppladdning",
    sellavo: "Komprimeras och roteras automatiskt, med bildstudio",
    status: "battre",
  },
  {
    label: "Betalt per annons",
    blocket: "89–249 kr i fordon/maskiner, 139 kr affärsverksamhet, ca 795 kr jobb",
    sellavo: "0 kr per annons i alla kategorier",
    status: "battre",
  },
  {
    label: "Prismodell",
    blocket: "Styckpris per annons, dynamiskt efter objektets värde",
    sellavo: `Abonnemang: gratis, ${PLANS.plus.priceLabel}, ${PLANS.pro.priceLabel} eller ${PLANS.expert.priceLabel}`,
    status: "battre",
  },
  {
    label: "Synlighet / lyft",
    blocket:
      "Paket (Plus/Premium) väljs vid publicering och priset styrs av objektets värde. Kan inte bytas i efterhand – bara enstaka tilläggsboostar köps till",
    sellavo: `Ingår: ${PLANS.plus.limits.promotionsPerMonth} lyft/mån i Plus, ${PLANS.pro.limits.promotionsPerMonth} i Pro, ${PLANS.expert.limits.promotionsPerMonth} i Expert – används när du vill`,
    status: "battre",
    note: "Blocket publicerar inga fasta kronpriser på paketen; de sätts efter varans pris.",
  },
  {
    label: "Annonsens livslängd",
    blocket: "365 dagar på Torget, sedan förnyelse (ny avgift i betalfack)",
    sellavo: "Ligger kvar tills du säljer, pausa och starta om när du vill",
    status: "battre",
  },
  {
    label: "Kontakt",
    blocket: "Telefonnummer exponeras ofta",
    sellavo: "Intern inkorg, inget nummer visas",
    status: "battre",
  },
  {
    label: "Plats",
    blocket: "Ort/postnummer",
    sellavo: "Karta med ungefärligt område",
    status: "battre",
  },
  {
    label: "Video i annonsen",
    blocket: "Ingen video för privatannonser",
    sellavo: "Video ingår från Pro, med ljudgranskning",
    status: "battre",
  },
  {
    label: "Filma rummet och få en lista",
    blocket: "Finns inte",
    sellavo: "Videoskanning som plockar ut prylar med värdeintervall",
    status: "battre",
  },
  {
    label: "Sparat bibliotek över dina prylar",
    blocket: "Finns inte",
    sellavo: "Mina prylar – publicera en och en när du vill",
    status: "battre",
  },
  {
    label: "Bildhjälp",
    blocket: "Du beskär och fixar själv",
    sellavo: "Rensa bakgrund, förbättra, ny vinkel, rotera – med versionshistorik",
    status: "battre",
  },
  {
    label: "Prisgranskning",
    blocket: "Ingen kontroll av att priset är rimligt",
    sellavo: "Vi flaggar priser som ser fel ut",
    status: "battre",
  },
  {
    label: "Ändringshistorik på annonsen",
    blocket: "Visas inte för köparen",
    sellavo: "Pris- och textändringar syns öppet",
    status: "battre",
  },
  {
    label: "Bevakningar och notiser på ny annons",
    blocket: "Sparade sökningar med push och mejl",
    sellavo: "Bevakningar med fritext, radie och prisintervall – notis i appen, push och mejl",
    status: "battre",
  },
  {
    label: "Spara favoriter",
    blocket: "Hjärta och sida med sparade annonser",
    sellavo: "Hjärta, sida med sparade annonser och räknare som säljaren ser",
    status: "lika",
  },
  {
    label: "Bud i flödet",
    blocket: "Strukturerade bud med acceptera/avböj",
    sellavo: "Bud med snabbval, budkort i tråden, notiser – plus toggel per annons",
    status: "lika",
  },
  {
    label: "Auktion med maxbud",
    blocket: "Finns inte",
    sellavo: "Auktion med automatiska maxbud, reservationspris och budgivning i realtid",
    status: "battre",
  },
  {
    label: "Betalning med köpskydd",
    blocket: "Betalning i appen, pengar hålls tills varan är godkänd",
    sellavo: "Hela flödet finns (order, spärrade pengar, godkännande, utbetalning) – simulerat",
    status: "delvis",
    note: "Kräver betalpartner med KYC innan riktiga pengar kan röra sig.",
  },
  {
    label: "Frakt med spårning",
    blocket: "Blocket Paket med fraktsedel och spårning",
    sellavo: "Fraktval, QR-kod och spårningsstatus i ordern – mot simulerad transportör",
    status: "delvis",
  },
  {
    label: "Tvistehantering",
    blocket: "Ärende via kundtjänst vid köpskyddsaffär",
    sellavo: "Tvist i affären med inlägg, eskalering och adminbeslut",
    status: "lika",
  },
  {
    label: "Support",
    blocket: "Kundtjänst och hjälpcenter",
    sellavo: "Ärendesystem med prioritering, svar i appen och mejl",
    status: "lika",
  },
  {
    label: "Appar och push",
    blocket: "iOS och Android med push",
    sellavo: "Installerbar app med egen push – men inte i butikerna",
    status: "delvis",
  },
  {
    label: "Företagssida",
    blocket: "Butikssida med statistik och lagerimport",
    sellavo: "Företagsprofil med verifiering och köpvillkor – ingen bulkimport",
    status: "delvis",
  },
  {
    label: "Fordonsdata på regnr",
    blocket: "Uppgifter fylls i automatiskt och används i värdering",
    sellavo: "Saknas – vi utgår från bilden",
    status: "sämre",
  },
  {
    label: "Kategorispecifika filter",
    blocket: "Egna fält per kategori: märke, modell, skick, storlek",
    sellavo: "Kategori, ort, pris och taggar",
    status: "sämre",
  },
  {
    label: "Verifierad identitet",
    blocket: "BankID för privatpersoner",
    sellavo: "Företag verifieras mot organisationsnummer; privatpersoner har e-postkod och Google",
    status: "delvis",
  },
  {
    label: "Bostad och jobb",
    blocket: "Egna vertikaler",
    sellavo: "Bara prylar och fordon – medvetet bortvalt",
    status: "sämre",
  },
  {
    label: "Räckvidd",
    blocket: "Mycket stor trafik",
    sellavo: "Ny tjänst, SEO-grunden på plats men liten volym",
    status: "sämre",
  },
];



const scenarios: { who: string; blocket: string; sellavo: string }[] = [
  {
    who: "Rensar garderoben – 4 annonser i Torget",
    blocket: "0 kr, men vill man synas kostar Plus/Premium per annons (pris efter varans värde)",
    sellavo: `0 kr på Gratis (${PLANS.free.limits.perMonth} annonser/mån ingår)`,
  },
  {
    who: "Säljer en bil",
    blocket: "från 249 kr, mer om bilen är värd mycket, plus paket för synlighet",
    sellavo: `0 kr – eller ${PLANS.plus.priceLabel} för Plus med ${PLANS.plus.limits.promotionsPerMonth} lyft i månaden`,
  },
  {
    who: "Aktiv säljare – 20 annonser i månaden med lyft",
    blocket: "Snabbt 300–500 kr per objekt i betalfack med paket och förnyelser",
    sellavo: `${PLANS.plus.priceLabel} totalt`,
  },
  {
    who: "Loppis-/småföretagare, 60+ annonser i månaden",
    blocket: "Företagspaket med månadsavgift, ofta tusenlappar",
    sellavo: `${PLANS.pro.priceLabel} med hög annonsgräns, eller ${PLANS.expert.priceLabel} för Expert`,
  },
  {
    who: "Vill sälja på auktion",
    blocket: "Finns inte för privatpersoner",
    sellavo: "0 kr – auktion med maxbud och reservationspris ingår",
  },
];

function Comparison() {
  return (
    <div className="space-y-10">
      <p className="max-w-3xl text-muted-foreground">
        Internt underlag för positionering och marknadsföring. Varje punkt är märkt med var vi
        faktiskt står idag – bättre, i nivå, delvis eller sämre. Prisuppgifterna är hämtade från
        Blockets publika prislista för privatannonser och deras beskrivning av annonspaket. Blocket
        ändrar priser löpande och använder dynamisk prissättning på både fordon och synlighetspaket
        – kontrollera innan siffrorna används i publik marknadsföring.
      </p>


      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-md border border-border bg-card p-5">
          <h2 className="font-heading text-lg font-semibold">Blocket – så många steg</h2>
          <ol className="mt-3 space-y-2 text-sm">
            {blocketSteps.map((s, i) => (
              <li key={s} className="flex gap-2 text-muted-foreground">
                <span className="w-5 shrink-0 text-right tabular-nums">{i + 1}.</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
          <p className="mt-4 flex items-center gap-2 text-sm font-medium text-destructive">
            <Clock className="h-4 w-4" /> ≈15 steg, 10–20 minuter
          </p>
        </div>

        <div className="rounded-md border border-primary/40 bg-primary/5 p-5">
          <h2 className="font-heading text-lg font-semibold">Sellavo – så enkelt är det</h2>
          <ol className="mt-3 space-y-2 text-sm">
            {sellavoSteps.map((s, i) => (
              <li key={s} className="flex gap-2">
                <span className="w-5 shrink-0 text-right tabular-nums text-primary">{i + 1}.</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
          <p className="mt-4 flex items-center gap-2 text-sm font-medium text-primary">
            <ArrowRight className="h-4 w-4" /> 3 steg, under en minut
          </p>
        </div>
      </section>

      <section>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-heading text-xl font-semibold">Det här tar Blocket betalt för</h2>
          <a
            href="https://blocket.zendesk.com/hc/sv/articles/22877545778962-Prislista-f%C3%B6r-privatpersoner"
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground hover:underline"
          >
            Blockets prislista <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Priset gäller per annons och avser en godkänd annons. Frånpris – för fordon sätts det
          slutliga priset efter objektets värde, så en dyr bil kostar mer än 249 kr. Hos oss kostar
          samma annons 0 kr oavsett kategori och oavsett vad varan är värd.

        </p>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {paidCategories.map((g) => (
            <div key={g.group} className="rounded-md border border-border bg-card p-4">
              <h3 className="flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-wide">
                <BadgeDollarSign className="h-4 w-4 text-destructive" /> {g.group}
              </h3>
              <ul className="mt-3 space-y-1.5 text-sm">
                {g.items.map((i) => (
                  <li key={i.name} className="flex items-baseline justify-between gap-3">
                    <span className="text-muted-foreground">{i.name}</span>
                    <span className="shrink-0 font-medium tabular-nums">{i.price}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-md border border-border bg-muted/30 p-4">
          <h3 className="flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-wide">
            <Info className="h-4 w-4" /> Gratis på Blocket (Torget)
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Kategorier som inte står i prislistan är gratis att annonsera i – men synligheten kostar
            ändå via paket:
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {freeCategories.map((c) => (
              <span
                key={c}
                className="rounded border border-border bg-background px-2 py-1 text-xs"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-heading text-xl font-semibold">
          Annonspaketen – uppgraderingar som kostar extra
        </h2>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Paketet väljs när annonsen skapas och kan inte köpas till eller bytas i efterhand. Hos oss
          ingår lyft i abonnemanget och kan användas när som helst.
        </p>
        <div className="mt-4 overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="whitespace-nowrap px-3 py-2">Paket</th>
                <th className="whitespace-nowrap px-3 py-2">Gäller</th>
                <th className="whitespace-nowrap px-3 py-2">Innehåll</th>
              </tr>
            </thead>
            <tbody>
              {packages.map((p) => (
                <tr key={`${p.name}-${p.segment}`} className="border-t border-border align-top">
                  <td className="px-3 py-3 font-medium">{p.name}</td>
                  <td className="px-3 py-3 text-muted-foreground">{p.segment}</td>
                  <td className="px-3 py-3 text-muted-foreground">{p.contains}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="font-heading text-xl font-semibold">Vad kostar det i praktiken?</h2>
        <div className="mt-4 overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="whitespace-nowrap px-3 py-2">Säljaren</th>
                <th className="whitespace-nowrap px-3 py-2">Blocket</th>
                <th className="whitespace-nowrap px-3 py-2">Sellavo</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map((s) => (
                <tr key={s.who} className="border-t border-border align-top">
                  <td className="px-3 py-3 font-medium">{s.who}</td>
                  <td className="px-3 py-3 text-muted-foreground">{s.blocket}</td>
                  <td className="px-3 py-3">
                    <span className="inline-flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      {s.sellavo}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="font-heading text-xl font-semibold">Punkt för punkt</h2>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Märkningen visar var vi står idag: bättre, i nivå, delvis på plats (finns men inte skarpt)
          eller sämre än Blocket.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {(["battre", "lika", "delvis", "sämre"] as RowStatus[]).map((s) => (
            <span
              key={s}
              className={`rounded px-2 py-1 text-xs font-medium ${rowStatusClass[s]}`}
            >
              {rowStatusLabel[s]}: {rows.filter((r) => r.status === s).length}
            </span>
          ))}
        </div>
        <div className="mt-4 overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="whitespace-nowrap px-3 py-2"></th>
                <th className="whitespace-nowrap px-3 py-2">Blocket</th>
                <th className="whitespace-nowrap px-3 py-2">Sellavo</th>
                <th className="whitespace-nowrap px-3 py-2">Läge</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const Icon = rowStatusIcon[r.status];
                return (
                  <tr key={r.label} className="border-t border-border align-top">
                    <td className="px-3 py-3 font-medium">{r.label}</td>
                    <td className="px-3 py-3 text-muted-foreground">{r.blocket}</td>
                    <td className="px-3 py-3">
                      <span className="inline-flex items-start gap-2">
                        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${rowStatusIconClass[r.status]}`} />
                        <span>
                          {r.sellavo}
                          {r.note && (
                            <span className="mt-1 block text-xs text-muted-foreground">
                              {r.note}
                            </span>
                          )}
                        </span>
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-semibold ${rowStatusClass[r.status]}`}
                      >
                        {rowStatusLabel[r.status]}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>


      <section>
        <h2 className="font-heading text-xl font-semibold">Funktion för funktion mot Blocket</h2>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Varje punkt är märkt med status: kvar att bygga, delvis på plats, eller levererat. Klicka
          på en punkt för att se vad som gjordes, vad som återstår och – där vi gått förbi dem – vad
          som är bättre hos oss.
        </p>
        <div className="mt-4">
          <ExternalCostSummary />
        </div>
        <BlocketGaps />
      </section>

      <CategoryFieldPlan />



      <section>
        <h2 className="font-heading text-xl font-semibold">Det här har vi som de inte har</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {ourEdge.map((e) => (
            <div key={e.title} className="rounded-md border border-primary/30 bg-primary/5 p-4">
              <h3 className="flex items-start gap-2 font-heading text-sm font-semibold">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                {e.title}
              </h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{e.text}</p>
            </div>
          ))}
        </div>
      </section>
      <CapacityAnalysis />

      <section className="rounded-md border border-border bg-card p-5">
        <h2 className="font-heading text-lg font-semibold">Budskap vi kan använda</h2>

        <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
          <li>«15 steg på Blocket. 3 hos oss.»</li>
          <li>«Fota. Klart. Sålt.»</li>
          <li>«Slipp skriva annonstexten – och slipp gissa priset.»</li>
          <li>«249 kr för en bilannons på Blocket. 0 kr här.»</li>
          <li>«Lyft ingår i abonnemanget – du behöver inte köpa Premium per annons.»</li>
          <li>«Bud på allt. Auktion på allt. Utan avgift.»</li>
          <li>«Ditt telefonnummer syns aldrig.»</li>
          <li>«Din annons dör inte efter 365 dagar.»</li>
          <li>«Filma rummet – vi listar allt du kan sälja och vad det är värt.»</li>
        </ul>
      </section>


    </div>
  );
}
